/**
 * @providesModule WebUnityFeatureVersionManager
 */

// Type representing the available features
type FeatureName = 'FTUE_SIGN_IN_TUTORIAL' | 'PROGRESSION_API';

// Class representing the feature version management for the Web Unity SDK
class WebUnityFeatureVersionManager {
  // Mapping of features to their corresponding required versions
  // Check last used tag in the repository when adding new feature.
  // Example:
  // git describe --tags --match '*.*.*' --abbrev=0
  // After feature release new higher tag will be available..
  private static unitySdkMinNotSupportedVersion: {[key in FeatureName]: string} = {
    FTUE_SIGN_IN_TUTORIAL: '2024.1.12',
    PROGRESSION_API: '2024.1.12',
  };

  private static readonly SDK_VERSION_KEY = '_sklz_gus';

  /**
   * Compares two version strings with flexible number of parts.
   * @param currentVersion - The current version (e.g. "2024.1.4.34", "2024.1.4").
   * @param minimumNotSupportedVersion - The required version to check against.
   * @returns True if the current version is greater than or equal to the required version; otherwise, false.
   */
  private static compareVersions(
    currentVersion: string,
    minimumNotSupportedVersion: string
  ): boolean {
    // Validate that versions contain only numbers and dots
    const versionRegex = /^[\d.]+$/;
    if (!versionRegex.test(currentVersion) || !versionRegex.test(minimumNotSupportedVersion)) {
      console.error('Invalid version format. Only numbers and dots are allowed');
      return false;
    }

    // Split versions into parts and convert to numbers
    const currentParts = currentVersion.split('.').map(Number);
    const requiredParts = minimumNotSupportedVersion.split('.').map(Number);

    // Validate that all parts are valid numbers
    if (currentParts.some(isNaN) || requiredParts.some(isNaN)) {
      console.error('Invalid version number detected');
      return false;
    }

    // Get the length of the longer version array
    const maxLength = Math.max(currentParts.length, requiredParts.length);

    // Compare each part, treating missing parts as 0
    for (let i = 0; i < maxLength; i++) {
      const current = currentParts[i] || 0;
      const required = requiredParts[i] || 0;

      if (current !== required) {
        return current > required;
      }
    }

    // All parts are equal
    return false;
  }

  /**
   * Gets the SDK version from localStorage
   * @returns The stored SDK version or undefined if not set
   */
  public static getVersion(): string | undefined {
    try {
      return localStorage.getItem(this.SDK_VERSION_KEY) || undefined;
    } catch (error) {
      console.error('Failed to get SDK version from localStorage:', error);
      return undefined;
    }
  }

  /**
   * Checks if the specified feature is available in the given SDK version.
   * @param feature - The feature to check.
   * @returns True if the feature is available in the specified SDK version; otherwise, false.
   */
  public static isFeatureSupported(feature: FeatureName): boolean {
    const minimumNotSupportedVersion = this.unitySdkMinNotSupportedVersion[feature];
    const currentVersion = this.getVersion();

    if (!currentVersion) {
      console.warn(`SDK version is not provided. Unable to check feature ${feature}.`);
      return false; // SDK version not provided
    }

    return this.compareVersions(currentVersion, minimumNotSupportedVersion); // Check version compatibility
  }
}

export default WebUnityFeatureVersionManager;
